import { VideoLibrary, Audiotrack, Code } from "@material-ui/icons";
import { buildCreativePreviewUrl, getCreativeTypeLabel } from "../../../utils/dataUtils";

// DCM creative type enums → friendly label/icon
// Used when an existing GCM creative is passed as the "file" (its `type` is a DCM enum,
// not a MIME type), e.g. via CreativeDialog.handleAssign or addDcmCreatives.
const DCM_TYPE_INFO = {
  INSTREAM_VIDEO: { label: "Video", icon: VideoLibrary },
  INSTREAM_AUDIO: { label: "Audio", icon: Audiotrack },
  IMAGE: { label: "Image", icon: null },
  // DISPLAY covers a static banner and a zipped H5 equally, so this is a
  // guess either way. "Image" is the better guess: of the ~70k active DISPLAY
  // creatives here with nothing else to go on, static images outnumber H5
  // roughly 1.6 to 1 — and artworkType below catches the H5 ones before the
  // fallback is reached. Labelling the remainder "Display" would have been
  // never-wrong but vague for the larger group.
  DISPLAY: { label: "Image", icon: null },
  DISPLAY_IMAGE_GALLERY: { label: "Image", icon: null },
  HTML5_BANNER: { label: "HTML5", icon: Code },
  RICH_MEDIA_DISPLAY_BANNER: { label: "HTML5", icon: Code },
  RICH_MEDIA_DISPLAY_EXPANDING: { label: "HTML5", icon: Code },
  RICH_MEDIA_DISPLAY_INTERSTITIAL: { label: "HTML5", icon: Code },
  RICH_MEDIA_DISPLAY_MULTI_FLOATING_INTERSTITIAL: { label: "HTML5", icon: Code },
  RICH_MEDIA_IM_EXPAND: { label: "HTML5", icon: Code },
  RICH_MEDIA_INPAGE: { label: "HTML5", icon: Code },
  RICH_MEDIA_INPAGE_FLOATING: { label: "HTML5", icon: Code },
  RICH_MEDIA_INTERSTITIAL_FLOAT: { label: "HTML5", icon: Code },
  RICH_MEDIA_MOBILE_INAPP: { label: "HTML5", icon: Code },
  RICH_MEDIA_PEEL_DOWN: { label: "HTML5", icon: Code },
  TRACKING_TEXT: { label: "Tracking", icon: null },
};

// Browsers don't always report a MIME type — .zip in particular comes through
// as "" on some Windows/browser combinations, which used to fall straight to
// "Unknown" and made H5 uploads render an unlabelled type. Fall back to the
// filename extension before giving up.
const EXTENSION_TYPE_INFO = [
  [/\.zip$/i, { label: "HTML5", icon: Code }],
  [/\.html?$/i, { label: "HTML5", icon: Code }],
  [/\.(mp4|mov|m4v|webm)$/i, { label: "Video", icon: VideoLibrary }],
  [/\.(mp3|m4a|wav|ogg|aac)$/i, { label: "Audio", icon: Audiotrack }],
  [/\.(png|jpe?g|gif|webp|svg)$/i, { label: "Image", icon: null }]
];

const getTypeInfoFromFileName = (name) => {
  if (!name || typeof name !== "string") return null;
  for (const [pattern, info] of EXTENSION_TYPE_INFO) {
    if (pattern.test(name)) return info;
  }
  return null;
};

// GCM classifies the asset itself in assetIdentifier.type — that beats sniffing
// the filename, which lies both ways (a static creative can serve through an
// .html wrapper, an H5 asset can be named anything).
const ASSET_KIND_INFO = {
  HTML: { label: "HTML5", icon: Code },
  IMAGE: { label: "Image", icon: null },
  // A static image served through an HTML wrapper — an image, not an H5, and
  // the most common primary asset type on DISPLAY creatives here. It resolved
  // correctly before only because its filename happened to end in .jpg; say
  // so explicitly rather than relying on that.
  HTML_IMAGE: { label: "Image", icon: null },
  VIDEO: { label: "Video", icon: VideoLibrary },
  AUDIO: { label: "Audio", icon: Audiotrack }
};

// Same asset lookup buildCreativePreviewUrl uses, with a fallback for creatives
// whose assets come back without an explicit role.
const getPrimaryAsset = (creative) => {
  const assets = creative.creativeAssets || [];
  return assets.find((a) => a.role === "PRIMARY") || assets[0] || null;
};

// The picker list labels existing creatives with getCreativeTypeLabel, which
// reads the taxonomy Format segment out of the creative name. These are the two
// Format values that say something the DISPLAY enum can't, so reusing that
// resolver keeps the workspace card from contradicting the row the user clicked.
// It also outranks the asset on purpose: GCM stores Format "Static" creatives
// with an HTML wrapper (assetIdentifier.type "HTML", artworkType
// ARTWORK_TYPE_HTML5), and a static backup image inherits its H5 primary's
// "H5S" name — in both cases the taxonomy is what the team traffics by.
const TAXONOMY_FORMAT_INFO = {
  H5S: { label: "HTML5", icon: Code },
  Static: { label: "Image", icon: null }
};

// Resolve a friendly label/icon from a DCM creative (or anything else shaped
// like one, e.g. the dcmCreative that CreativeDialog.handleAssign stores as the
// asset's `file`). Returns null when the type isn't one we have a label for.
export const getDcmCreativeTypeInfo = (creative) => {
  if (!creative || !creative.type) return null;
  if (creative.type === "HTML5_BANNER") return { label: "HTML5", icon: Code };

  const listLabel = getCreativeTypeLabel(creative.type, null, creative.name || "");
  if (TAXONOMY_FORMAT_INFO[listLabel]) return TAXONOMY_FORMAT_INFO[listLabel];

  // artworkType is CM360's own read-only classification and the only signal
  // here that does not depend on either the taxonomy or creativeAssets being
  // present. The API documents it as rich-media-only, but it is populated on
  // roughly 40k active DISPLAY creatives in this account, two thirds of which
  // carry no Format segment to read.
  if (creative.artworkType === "ARTWORK_TYPE_HTML5") {
    return { label: "HTML5", icon: Code };
  }

  // The name carries no Format we recognise (an externally-created creative, or
  // one named outside the taxonomy). DISPLAY is the one enum that covers both a
  // static image and a zipped H5, so read the asset before settling for it.
  if (creative.type === "DISPLAY") {
    const primaryAsset = getPrimaryAsset(creative);
    const assetId = (primaryAsset && primaryAsset.assetIdentifier) || {};
    if (ASSET_KIND_INFO[assetId.type]) return ASSET_KIND_INFO[assetId.type];
    const assetInfo = assetId.name ? getTypeInfoFromFileName(assetId.name) : null;
    if (assetInfo) return assetInfo;
  }

  return DCM_TYPE_INFO[creative.type] || null;
};

export const getFileTypeInfo = (file) => {
  if (!file) return { label: "Unknown", icon: null };
  if (!file.type) {
    return getTypeInfoFromFileName(file.name) || { label: "Unknown", icon: null };
  }
  if (file.type.startsWith("image/")) return { label: "Image", icon: null };
  if (file.type.startsWith("video/")) return { label: "Video", icon: VideoLibrary };
  if (file.type.startsWith("audio/")) return { label: "Audio", icon: Audiotrack };
  if (file.type.includes("zip") || file.type.includes("application")) return { label: "HTML5", icon: Code };
  // Fallback: DCM creative type enum (e.g. when the asset.file is actually a dcmCreative)
  const dcmInfo = getDcmCreativeTypeInfo(file);
  if (dcmInfo) return dcmInfo;
  return getTypeInfoFromFileName(file.name) || { label: "Unknown", icon: null };
};

// Asset-aware variant: an existing GCM creative is authoritative about its own
// type, so it is consulted first — addDcmCreatives builds a synthetic `file` with
// no name and no MIME type, which would otherwise resolve to "Unknown" and then
// to the bare DISPLAY enum, mislabelling an H5 creative as "Image".
// Falls back to file.type (MIME) for freshly uploaded files.
export const getAssetTypeInfo = (asset) => {
  if (!asset) return { label: "Unknown", icon: null };
  const dcmInfo = getDcmCreativeTypeInfo(asset.dcmCreative);
  if (dcmInfo) return dcmInfo;
  return getFileTypeInfo(asset.file);
};

// A freshly uploaded File already has a usable object-URL in asset.preview.
// An existing DCM creative (picked via "Choose from existing") has neither
// a File nor a preview — this builds one on-the-fly from its
// creativeAssets, reusing the same lookup that already powers the
// PlacementList/ChangeLog "eye icon" preview dialog. Returns
// { url, previewType } or null if no preview can be built.
export const resolveExistingCreativePreview = (asset) => {
  if (!asset || !asset.dcmCreative) return null;
  return buildCreativePreviewUrl(asset.dcmCreative);
};
